import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

/**
 * Simplified Farcaster Context for managing user state from Farcaster SDK
 * 
 * This context handles:
 * - User authentication state from Farcaster SDK
 * - Basic user profile management
 * - Simple state persistence
 * 
 * Note: We use the official Farcaster SDK for all authentication and data
 */

// Initial state structure
const initialState = {
  // User authentication from Farcaster SDK
  currentUser: null,
  isAuthenticated: false,
  
  // UI state
  isLoading: false,
  error: null,
};

// Action types
const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_CURRENT_USER: 'SET_CURRENT_USER',
  CLEAR_ERROR: 'CLEAR_ERROR',
  LOGOUT: 'LOGOUT',
};

// Reducer function
const farcasterReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return { ...state, isLoading: action.payload };
      
    case ActionTypes.SET_ERROR:
      return { ...state, error: action.payload, isLoading: false };
      
    case ActionTypes.CLEAR_ERROR:
      return { ...state, error: null };
      
    case ActionTypes.SET_CURRENT_USER:
      return { 
        ...state, 
        currentUser: action.payload,
        isAuthenticated: !!action.payload,
        error: null 
      };
      
    case ActionTypes.LOGOUT:
      return {
        ...initialState,
      };
      
    default:
      return state;
  }
};

// Create context
const FarcasterContext = createContext();

// Provider component
export const FarcasterProvider = ({ children }) => {
  const [state, dispatch] = useReducer(farcasterReducer, initialState);

  // Helper functions
  const setLoading = useCallback((loading) => {
    dispatch({ type: ActionTypes.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: ActionTypes.SET_ERROR, payload: error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: ActionTypes.CLEAR_ERROR });
  }, []);

  // Authentication functions (simplified for Farcaster SDK)
  const setCurrentUser = useCallback((user) => {
    try {
      setLoading(true);
      clearError();
      
      if (!user) {
        throw new Error('Invalid user data provided');
      }
      
      // Format user data consistently
      const formattedUser = {
        fid: user.fid,
        username: user.username || user.handle,
        displayName: user.displayName || user.display_name || user.username,
        bio: user.bio || '',
        pfpUrl: user.pfpUrl || user.pfp_url,
        verified: user.verified || false,
        address: user.address || user.verifications?.[0]?.address,
        followerCount: user.followerCount || user.follower_count || 0,
        followingCount: user.followingCount || user.following_count || 0
      };
      
      dispatch({ type: ActionTypes.SET_CURRENT_USER, payload: formattedUser });
      
      // Store authentication in localStorage
      localStorage.setItem('farcaster_user', JSON.stringify(formattedUser));
      
      return formattedUser;
    } catch (error) {
      console.error('Error setting current user:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [setLoading, clearError, setError]);

  const logout = useCallback(() => {
    // Clear authentication data
    localStorage.removeItem('farcaster_user');
    dispatch({ type: ActionTypes.LOGOUT });
  }, []);

  // Load persisted user on mount
  useEffect(() => {
    const loadPersistedUser = () => {
      try {
        const storedUser = localStorage.getItem('farcaster_user');
        if (storedUser) {
          const user = JSON.parse(storedUser);
          dispatch({ type: ActionTypes.SET_CURRENT_USER, payload: user });
        }
      } catch (error) {
        console.warn('Failed to load persisted user:', error);
        localStorage.removeItem('farcaster_user');
      }
    };

    loadPersistedUser();
  }, []);

  // Context value
  const value = {
    // State
    ...state,
    
    // Actions
    setCurrentUser,
    logout,
    setError,
    clearError,
    setLoading
  };

  return (
    <FarcasterContext.Provider value={value}>
      {children}
    </FarcasterContext.Provider>
  );
};

// Hook to use the context
export const useFarcaster = () => {
  const context = useContext(FarcasterContext);
  if (!context) {
    throw new Error('useFarcaster must be used within a FarcasterProvider');
  }
  return context;
};

export default FarcasterContext; 